import React, { useCallback, useEffect, useRef } from 'react';
import { Box, Grid, Paper } from '@material-ui/core';
import { Form } from '@unform/web';
import { toast } from 'react-toastify';
import { useHistory, useParams } from 'react-router-dom';
import {
  extractValidationErrors,
  GetUser,
  RegisterUser,
  ValidationError,
  signupUserRegistrationForm,
  editUserForm,
} from '@bforms-web/common';

import { USERS_ROUTE } from '../../../constants';
import { useLoading } from '../../../contexts/loading.context';
import Button from '../../Button';
import Input from '../../Input';
import PageTitle from '../../PageTitle';

import { useStyles } from './styles';
import UserTypeSelect from '../../UserTypeSelect';

export type UserRegisterProps = {
  getUser: GetUser;
  registerUser: RegisterUser;
};

export default function UserRegister({ getUser, registerUser }: UserRegisterProps): JSX.Element {
  const classes = useStyles();
  const formRef = useRef({} as any);
  const { isLoading, showLoading, hideLoading } = useLoading();
  const { push } = useHistory();
  const { id } = useParams<{ id: string }>();

  const handleSubmit = useCallback(
    (data) => {
      showLoading();
      async function runAsync() {
        try {
          formRef.current.setErrors({});
          if(!id){
            await signupUserRegistrationForm.validate(data, {
              abortEarly: false,
            });
          }else{
            await editUserForm.validate(data, {
              abortEarly: false,
            });
          }
          await registerUser.execute({
            id,
            ...data,
          });
          toast.success('Registrado com sucesso');
          push(USERS_ROUTE);
        } catch (err) {
          console.log(err);
          let errors = {};
          if (err instanceof ValidationError) {
            errors = extractValidationErrors(err);
            formRef.current.setErrors(errors);
          } else {
            toast.error('Falha ao registrar usuário');
          }
        } finally {
          hideLoading();
        }
      }
      runAsync();
    },
    [id],
  );

  useEffect(() => {
    if (!id) return;
    showLoading();
    async function runAsync() {
      try {
        const user = await getUser.execute(id);
        formRef.current.setData(user);
      } catch (err) {
        toast.error('Falha ao carregar usuário');
        push(USERS_ROUTE);
      } finally {
        hideLoading();
      }
    }
    runAsync();
  }, [id]);  
  
  return (
    <Box className={classes.container}>
      <PageTitle>{!id ? 'Cadastro de usuário' : 'Editar usuário'}</PageTitle>
      <br />
      <Form ref={formRef} onSubmit={handleSubmit}>
        <Paper className={classes.container}>
          <Grid container spacing={2} direction="column">
            <Grid item xs={12} sm="auto">
              <Input               
                name="name"
                variant="outlined"
                label={'Nome'}
                className={classes.input}
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={12} sm="auto">
              <Input  
                name="email"
                variant="outlined"
                label={'Email'}
                className={classes.input}
                disabled={isLoading}
              />
            </Grid>
            <Grid item xs={12} sm="auto">
              <UserTypeSelect
                name="type"
                label="Tipo de usuário"
                className={classes.input}
                disabled={isLoading} 
                variant="outlined"
              />
            </Grid>
            {!id &&(
            <Grid item xs={12} sm="auto">
              <Input         
                name="password"
                variant="outlined"
                type="password"
                label={'Senha'}
                className={classes.input}
                disabled={isLoading}
              />
            </Grid>
            )}
            {!id &&(
              <Grid item xs={12} sm="auto">
              <Input             
                name="confirmPassword"
                variant="outlined"
                type="password"
                label={'Confirme a senha'}
                className={classes.input}
                disabled={isLoading}
              />
            </Grid>
            )}
            <Grid item>
              <Button type="submit" variant="contained" color="primary">
                {'Salvar'}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Form>
    </Box>
  );
}
